import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { useIntegrations } from '../../hooks/useIntegrations';
import { connectGoogle, setCalendarPreference } from '../../services/integration.service';

const ERROR_MESSAGES = {
  google_denied:  'Google sign-in was cancelled.',
  google_invalid: 'Google sign-in failed — please try again.',
  google_expired: 'Google sign-in session expired — please try again.',
  google_failed:  'Could not connect Google Calendar. Please try again.',
};

const SUCCESS_MESSAGES = {
  google_calendar: '✅ Google Calendar connected! Choose below what you\'d like it to do.',
};

// ─── Coming Soon Card ──────────────────────────────────────────────────────────

function ComingSoonCard({ icon, name, description, dataPoints }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 opacity-75">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <p className="font-semibold text-reset-brown text-sm">{name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <span className="shrink-0 text-xs font-medium bg-gray-100 text-gray-500 rounded-full px-3 py-1">
          Coming soon
        </span>
      </div>
      {dataPoints && (
        <ul className="mt-3 ml-9 space-y-0.5 text-xs text-gray-400">
          {dataPoints.map(d => <li key={d}>· {d}</li>)}
        </ul>
      )}
    </div>
  );
}

// ─── In Development Card ───────────────────────────────────────────────────────

function InDevCard({ icon, name, description, dataPoints }) {
  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <p className="font-semibold text-reset-brown text-sm">{name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <span className="shrink-0 text-xs font-medium bg-amber-100 text-amber-700 rounded-full px-3 py-1">
          In development
        </span>
      </div>
      {dataPoints && (
        <ul className="mt-3 ml-9 space-y-0.5 text-xs text-gray-500">
          {dataPoints.map(d => <li key={d}>· {d}</li>)}
        </ul>
      )}
    </div>
  );
}

// ─── Live Integration Card ─────────────────────────────────────────────────────

function LiveCard({ icon, name, description, connected, lastSync, onConnect, onDisconnect, dataPoints }) {
  const syncText = lastSync
    ? `Last synced ${new Date(lastSync).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}`
    : 'Never synced';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <p className="font-semibold text-reset-brown text-sm">{name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {connected ? (
            <div className="space-y-1">
              <span className="block text-xs font-medium bg-green-100 text-green-700 rounded-full px-3 py-1">
                ✓ Connected
              </span>
              <button
                onClick={onDisconnect}
                className="text-[11px] text-gray-400 hover:text-red-500 transition block text-right w-full"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={onConnect}
              className="text-xs font-medium bg-reset-brown text-white rounded-xl px-4 py-2 hover:opacity-90 transition"
            >
              Connect
            </button>
          )}
        </div>
      </div>
      {connected && (
        <p className="text-[11px] text-gray-400 mt-2 ml-9">{syncText}</p>
      )}
      {dataPoints && (
        <ul className="mt-3 ml-9 space-y-0.5 text-xs text-gray-500">
          {dataPoints.map(d => <li key={d}>· {d}</li>)}
        </ul>
      )}
    </div>
  );
}

// ─── Google Calendar opt-ins ────────────────────────────────────────────────
// Three real, separate member choices (2026-09-18) — connecting the
// calendar at all only turns on the background read-only sync; each of
// these is its own explicit opt-in on top of that, per Jenn's direct spec.

function ToggleSwitch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50 ${checked ? 'bg-reset-terracotta' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  );
}

function CalendarOptInRow({ title, sub, checked, onToggle, saving, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-t border-gray-100 first:border-t-0">
      <div className="flex-1">
        <p className="text-sm font-medium text-reset-brown">{title}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        {children}
      </div>
      <ToggleSwitch checked={checked} onChange={onToggle} disabled={saving} label={title} />
    </div>
  );
}

function GoogleCalendarPreferences({ status, onRefresh }) {
  const [saving, setSaving] = useState(null); // which field is mid-save, or null
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  const handleToggle = async (field, value) => {
    setSaving(field);
    setError(null);
    setWarning(null);
    setNeedsReconnect(false);
    try {
      const { warning: w } = await setCalendarPreference({ [field]: value });
      if (w) setWarning(w);
      onRefresh();
    } catch (err) {
      if (err.response?.data?.code === 'NEEDS_RECONNECT') {
        setNeedsReconnect(true);
      } else {
        setError(err.response?.data?.error || 'Could not save this — please try again.');
      }
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-reset-creamDark rounded-2xl p-4 mt-3 ml-9">
      <p className="text-xs font-bold text-reset-brownDeep mb-1">What should your connected calendar do?</p>
      <p className="text-xs text-gray-500 mb-1">Each of these is your own choice — turning on the connection above doesn't turn any of these on by itself.</p>

      <CalendarOptInRow
        title="Show my Google Calendar items in What's on Tap for Today"
        sub="Only today's items from your calendar — not past or future days."
        checked={status.show_in_what_on_tap}
        onToggle={(v) => handleToggle('showInWhatOnTap', v)}
        saving={saving === 'showInWhatOnTap'}
      >
        {/* Real image not supplied yet — placeholder only, needs real alt
            text the moment a real image is added here (accessibility rule). */}
        <div className="mt-2 w-24 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400 text-center px-1">
          Image needed here
        </div>
      </CalendarOptInRow>

      <CalendarOptInRow
        title="Show my What's on Tap items as checkable to-do's in my Google Calendar"
        sub="Each item is its own real checkbox at the top of your day. Check one off in Google and it checks off on your Hub Dashboard too."
        checked={status.push_what_on_tap}
        onToggle={(v) => handleToggle('pushWhatOnTap', v)}
        saving={saving === 'pushWhatOnTap'}
      />

      <CalendarOptInRow
        title="Put a My Hub to-do at the top of today in my Google Calendar"
        sub="A 😊 Open My Hub item at the top of your day — tap it, tap the link, you're in."
        checked={status.show_happy_face}
        onToggle={(v) => handleToggle('showHappyFace', v)}
        saving={saving === 'showHappyFace'}
      />

      {(status.push_what_on_tap || status.show_happy_face) && (
        <p className="text-[11px] text-gray-400 mt-2">
          These show up as Google <em>Tasks</em>, not events — if you don't see them on your calendar, check that "Tasks" is switched on under "My calendars" in Google Calendar's own sidebar (it's on by default for most people).
        </p>
      )}

      {needsReconnect && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
          This needs broader calendar access than you granted when you first connected.
          <button type="button" onClick={connectGoogle} className="ml-2 font-bold underline">Reconnect Google Calendar</button>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {warning && <p className="text-xs text-amber-600 mt-2">{warning}</p>}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function Integrations() {
  const { status, isLoading, error, disconnect, refresh } = useIntegrations();
  const [searchParams, setSearchParams] = useSearchParams();

  const connectedParam = searchParams.get('connected');
  const errorParam     = searchParams.get('error');

  useEffect(() => {
    if (connectedParam || errorParam) {
      if (connectedParam) refresh();
      const timer = setTimeout(() => setSearchParams({}), 4000);
      return () => clearTimeout(timer);
    }
  }, [connectedParam, errorParam, refresh, setSearchParams]);


  return (
    <DashboardLayout>
      <div className="mb-6">
        <PageHeading
          title="My Devices"
          subtitle="Connect your wearables and calendar so ResetOS can watch your data automatically — you won't have to enter anything manually."
        />
      </div>

      {/* OAuth result banners */}
      {connectedParam && SUCCESS_MESSAGES[connectedParam] && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3 text-sm">
          {SUCCESS_MESSAGES[connectedParam]}
        </div>
      )}
      {errorParam && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {ERROR_MESSAGES[errorParam] || 'Something went wrong — please try again.'}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {isLoading && !status ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-2xl" />)}
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── Calendars ── */}
          <div>
            <h2 className="text-sm font-semibold text-reset-brown mb-3 uppercase tracking-wide">Calendars</h2>
            <div className="space-y-3">

              <LiveCard
                icon="📅"
                name="Google Calendar"
                description="Connect it, then choose below what you'd like it to do."
                connected={status?.google_calendar?.connected || false}
                lastSync={status?.google_calendar?.last_sync_at}
                onConnect={connectGoogle}
                onDisconnect={() => disconnect('google_calendar')}
                dataPoints={['Events auto-tagged to sectors', 'Syncs 7 days back, 7 days forward']}
              />
              {status?.google_calendar?.connected && (
                <GoogleCalendarPreferences status={status.google_calendar} onRefresh={refresh} />
              )}

              <ComingSoonCard
                icon="📆"
                name="Apple Calendar"
                description="Connect your Apple Calendar to sync events automatically."
                dataPoints={['Same sector tagging as Google', 'Works on iPhone and Mac']}
              />

              <ComingSoonCard
                icon="📧"
                name="Outlook / Microsoft 365 Calendar"
                description="Connect your work or personal Outlook calendar."
                dataPoints={['Events synced via Microsoft Graph API', 'Sector tagging included']}
              />
            </div>
          </div>

          {/* ── Wearables ── */}
          <div>
            <h2 className="text-sm font-semibold text-reset-brown mb-3 uppercase tracking-wide">Wearables & Health</h2>
            <div className="space-y-3">

              <InDevCard
                icon="🍎"
                name="Apple Health"
                description="Pull sleep, heart rate, activity, and more from your iPhone or Apple Watch."
                dataPoints={['Sleep hours & quality', 'Heart rate & HRV', 'Steps & active minutes', 'Pushes each morning via Shortcut']}
              />

              <ComingSoonCard
                icon="⌚"
                name="Fitbit"
                description="Connect your Fitbit tracker to sync sleep and activity data automatically."
                dataPoints={['Sleep hours & quality', 'HRV (Heart Rate Variability)', 'Resting heart rate', 'Steps & active minutes']}
              />

              <ComingSoonCard
                icon="💍"
                name="Oura Ring"
                description="Pull readiness, sleep, and activity scores from your Oura Ring."
                dataPoints={['Readiness score', 'Sleep quality & duration', 'Activity & recovery data']}
              />

              <ComingSoonCard
                icon="📡"
                name="Other Wearables"
                description="Support for Whoop, Garmin, Polar, and more is on the roadmap."
                dataPoints={['Whoop strain & recovery', 'Garmin activity metrics', 'Polar heart rate data']}
              />
            </div>
          </div>

          {/* Note */}
          <div className="bg-reset-cream border border-reset-terracotta/20 rounded-2xl p-4 text-xs text-gray-600">
            <p className="font-medium text-reset-brown mb-1">A note on integrations</p>
            <p>
              Integrations in development are being built and tested. Connecting a device before
              integration is complete may not pull data correctly. We'll notify you when each
              integration is ready to use. You can always enter data manually through your daily
              Compass check-in.
            </p>
          </div>

        </div>
      )}
    </DashboardLayout>
  );
}
